import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";
import { createWebUser, setAuthCookies } from "@/services/authService";
import { setUserProfile } from "@/utils/authStorage";
import {
  useAuthViewModel,
  type AuthStep,
  type AuthSuccessContext,
} from "@/viewmodels/auth/useAuthViewModel";
import { OtpStep } from "@/components/auth/steps/OtpStep";
import type { RideDetails } from "@/types/rideDetails";

export type { AuthStep };

interface BookingAuthStepsProps {
  ride: RideDetails;
  selectedSeats: number;
  onAuthenticated: (context: AuthSuccessContext) => void;
  onStepChange?: (step: AuthStep) => void;
  onClose: () => void;
}

/** Phone → OTP → registration flow for the ride booking modal. Remount to reset. */
export function BookingAuthSteps({
  ride,
  selectedSeats,
  onAuthenticated,
  onStepChange,
  onClose,
}: BookingAuthStepsProps) {
  const { toast } = useToast();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [gender, setGender] = useState("Male");
  const [registering, setRegistering] = useState(false);

  const vm = useAuthViewModel("register", onClose, {
    onAuthenticated,
  });

  useEffect(() => {
    onStepChange?.(vm.step);
  }, [vm.step, onStepChange]);

  const handleCompleteRegistration = async () => {
    if (!firstName.trim() || !lastName.trim()) {
      toast({
        title: "Missing details",
        description: "Please enter your first and last name.",
        variant: "destructive",
        duration: 3000,
      });
      return;
    }

    const otpAccessToken = vm.pendingTokens?.accessToken;
    if (!otpAccessToken) {
      toast({
        title: "Session expired",
        description: "Please verify your number again.",
        variant: "destructive",
        duration: 3000,
      });
      return;
    }

    setRegistering(true);
    try {
      const { accessToken, refreshToken } = await createWebUser({
        phoneNumber: vm.phoneNumber,
        countryCode: vm.countryCode,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        gender,
        accessToken: otpAccessToken,
      });

      setAuthCookies({ accessToken, refreshToken, deviceId: vm.phoneNumber });
      setUserProfile({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        gender,
        phoneNumber: vm.phoneNumber,
        callingCode: vm.countryCode,
      });

      onAuthenticated({
        userStatus: "NewUser",
        phoneNumber: vm.phoneNumber,
        countryCode: vm.countryCode,
      });
    } catch (e: any) {
      toast({
        title: "Registration failed",
        description: e?.message || "Please try again.",
        variant: "destructive",
        duration: 3000,
      });
    } finally {
      setRegistering(false);
    }
  };

  return (
    <>
      {vm.step === "phone" && (
        <div className="space-y-6">
          <p className="text-sm text-gray-600">
            Ride{" "}
            <span className="font-semibold text-gray-900">{ride.rideId}</span>
            {" · "}
            {selectedSeats} {selectedSeats === 1 ? "seat" : "seats"}
          </p>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">
              Phone Number
            </label>
            <div className="flex w-full">
              <div className="relative z-10" ref={vm.countryDropdownRef}>
                <button
                  type="button"
                  className="flex h-12 items-center gap-2 rounded-l-xl border border-r-0 border-gray-300 bg-white pl-3 pr-6 text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  onClick={() =>
                    vm.setCountryDropdownOpen(!vm.countryDropdownOpen)
                  }
                  tabIndex={0}
                  style={{ minWidth: 90, maxWidth: 140 }}
                >
                  {vm.selectedCountry && (
                    <>
                      <span className="font-semibold">
                        {vm.selectedCountry.callingCode}
                      </span>
                      <img
                        src={`https://flagcdn.com/24x18/${vm.selectedCountry.code.toLowerCase()}.png`}
                        alt="flag"
                        className="ml-2 inline-block h-4 w-5 rounded-sm border border-gray-200 bg-white object-cover"
                      />
                    </>
                  )}
                </button>
                {vm.countryDropdownOpen && (
                  <div
                    className="animate-fade-in absolute left-0 right-0 z-50 mt-2 w-[320px] overflow-y-auto overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-lg"
                    style={{ maxHeight: "200px" }}
                  >
                    <div className="sticky top-0 border-b border-gray-100 bg-white p-2">
                      <input
                        type="text"
                        placeholder="Search country or code"
                        value={vm.countrySearch}
                        onChange={(e) => vm.setCountrySearch(e.target.value)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        autoFocus
                      />
                    </div>
                    <ul className="divide-y divide-gray-100">
                      {vm.filteredCountries.length === 0 && (
                        <li className="p-4 text-center text-gray-500">
                          No countries found
                        </li>
                      )}
                      {vm.filteredCountries.slice(0, 20).map((c) => (
                        <li
                          key={c.callingCode}
                          className={`flex cursor-pointer items-center gap-3 px-4 py-2 hover:bg-emerald-50 ${
                            vm.countryCode === c.callingCode
                              ? "bg-emerald-100"
                              : ""
                          }`}
                          onClick={() => vm.handleCountrySelect(c)}
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              vm.handleCountrySelect(c);
                            }
                          }}
                        >
                          <span className="font-semibold">{c.callingCode}</span>
                          <img
                            src={`https://flagcdn.com/24x18/${c.code.toLowerCase()}.png`}
                            alt={c.name}
                            className="ml-2 inline-block h-4 w-5 rounded-sm border border-gray-200 bg-white object-cover"
                          />
                          <span className="ml-2">{c.name}</span>
                        </li>
                      ))}
                      {vm.filteredCountries.length > 20 && (
                        <li className="p-2 text-center text-xs text-gray-400">
                          Showing first 20 results. Refine your search for more.
                        </li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
              <input
                type="tel"
                placeholder="Phone number"
                value={vm.phoneNumber}
                onChange={(e) => {
                  const digits = e.target.value
                    .replace(/[^0-9]/g, "")
                    .slice(0, 10);
                  vm.setPhoneNumber(digits);
                }}
                maxLength={10}
                className="h-12 flex-1 rounded-r-xl border border-l-0 border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                style={{ minWidth: 0 }}
              />
            </div>
          </div>

          <Button
            onClick={vm.handlePhoneSubmit}
            disabled={vm.loading}
            className="h-12 w-full bg-emerald-600 text-base font-bold hover:bg-emerald-700"
          >
            {vm.loading ? "Sending OTP..." : "Continue"}
          </Button>
        </div>
      )}

      {vm.step === "otp" && <OtpStep vm={vm} />}

      {vm.step === "role" && (
        <div className="space-y-5">
          <p className="text-sm text-gray-600">
            Just a few details to complete your booking.
          </p>

          <div>
            <Label htmlFor="bookingFirstName">First Name</Label>
            <input
              id="bookingFirstName"
              type="text"
              placeholder="Enter your first name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="mt-1 w-full rounded-xl border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>

          <div>
            <Label htmlFor="bookingLastName">Last Name</Label>
            <input
              id="bookingLastName"
              type="text"
              placeholder="Enter your last name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="mt-1 w-full rounded-xl border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>

          <div className="space-y-1">
            <Label>Gender</Label>
            <Select value={gender} onValueChange={setGender}>
              <SelectTrigger>
                <SelectValue placeholder="Select gender" />
              </SelectTrigger>
              <SelectContent position="popper">
                <SelectItem value="Male">Male</SelectItem>
                <SelectItem value="Female">Female</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
                <SelectItem value="Prefer not to say">
                  Prefer not to say
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Button
            onClick={handleCompleteRegistration}
            disabled={registering || !firstName.trim() || !lastName.trim()}
            className="h-12 w-full bg-emerald-600 text-base font-bold hover:bg-emerald-700"
          >
            {registering ? "Please wait..." : "Complete Registration"}
          </Button>
        </div>
      )}
    </>
  );
}
